import { Dimensions, ScaledSize } from 'react-native';
import { useState, useEffect, useMemo } from 'react';

// Get initial dimensions
const { width: initialWidth, height: initialHeight } = Dimensions.get('window');

// ============================================
// BREAKPOINTS
// ============================================
export const BREAKPOINTS = {
  narrow: 360,    // < 360px (very narrow phones, speech bubbles need smaller text)
  mobile: 480,    // 360px - 480px
  tablet: 768,    // 480px - 768px
  desktop: 1024,  // 768px - 1024px
  large: 1200,    // 1024px - 1920px
  ultrawide: 1920, // > 1920px (2560px, 4K, etc.)
} as const;

// ============================================
// CHARACTER DISPLAY HEIGHT CONSTRAINTS
// ============================================
// Divider position: 15-20% from bottom (like a battery indicator)
// This means character display takes 80-85% of screen height
export const CHARACTER_HEIGHT = {
  MIN_PERCENT: 0.2,       // Minimum 80% of screen (divider at 20% from bottom)
  MAX_PERCENT: 0.9,       // Maximum 85% of screen (divider at 15% from bottom)
  DEFAULT_PERCENT: 0.85,   // Default 82% of screen (divider at 18% from bottom)
  ABSOLUTE_MIN_PX: 200,    // Never go below 200px regardless of screen size
} as const;

// ============================================
// DEVICE TYPE DETECTION
// ============================================
export type DeviceType = 'narrow' | 'mobile' | 'tablet' | 'desktop' | 'large' | 'ultrawide';

export function getDeviceType(width: number): DeviceType {
  if (width < BREAKPOINTS.narrow) return 'narrow';
  if (width < BREAKPOINTS.mobile) return 'mobile';
  if (width < BREAKPOINTS.tablet) return 'tablet';
  if (width < BREAKPOINTS.desktop) return 'desktop';
  if (width < BREAKPOINTS.large) return 'large';
  return 'ultrawide';
}

// ============================================
// RESPONSIVE FONT SIZES
// ============================================
export interface ResponsiveFonts {
  xs: number;   // 10-12px (badges, timestamps, captions)
  sm: number;   // 12-14px (secondary text, labels)
  md: number;   // 14-16px (body text, inputs)
  lg: number;   // 16-20px (character names, emphasis)
  xl: number;   // 18-24px (subheadings)
  xxl: number;  // 24-32px (headings)
  xxxl: number; // 28-40px (large titles)
}

export function calculateFonts(width: number): ResponsiveFonts {
  // Scale factor based on screen width
  // Narrow (<360): 0.75, Mobile (360-480): 0.85, Tablet (480-768): 0.92, Desktop (768-1024): 1.0, Large (1024-1920): 1.1, Ultrawide (>1920): 1.45
  const scale = width < BREAKPOINTS.narrow ? 0.75
    : width < BREAKPOINTS.mobile ? 0.85
    : width < BREAKPOINTS.tablet ? 0.92
    : width < BREAKPOINTS.desktop ? 1.0
    : width < BREAKPOINTS.large ? 1.1
    : 1.45; // Ultrawide: 2560px and up

  return {
    xs: Math.round(11 * scale),     // 8-11px
    sm: Math.round(12 * scale),     // 9-13px
    md: Math.round(14 * scale),     // 10-15px (speech bubble text)
    lg: Math.round(16 * scale),     // 12-18px
    xl: Math.round(20 * scale),     // 15-22px
    xxl: Math.round(24 * scale),    // 18-26px
    xxxl: Math.round(32 * scale),   // 24-35px
  };
}

// ============================================
// RESPONSIVE SPACING
// ============================================
export interface ResponsiveSpacing {
  xs: number;   // 4px base
  sm: number;   // 8px base
  md: number;   // 12px base
  lg: number;   // 16px base
  xl: number;   // 24px base
  xxl: number;  // 32px base
  xxxl: number; // 48px base
}

export function calculateSpacing(width: number): ResponsiveSpacing {
  // Spacing scale: smaller on narrow/mobile, larger on desktop, even larger on ultrawide
  const scale = width < BREAKPOINTS.narrow ? 0.75
    : width < BREAKPOINTS.mobile ? 0.85
    : width < BREAKPOINTS.tablet ? 0.92
    : width < BREAKPOINTS.desktop ? 1.0
    : width < BREAKPOINTS.large ? 1.1
    : 1.45; // Ultrawide: 2560px and up

  return {
    xs: Math.round(4 * scale),
    sm: Math.round(8 * scale),
    md: Math.round(12 * scale),
    lg: Math.round(16 * scale),
    xl: Math.round(24 * scale),
    xxl: Math.round(32 * scale),
    xxxl: Math.round(48 * scale),
  };
}

// ============================================
// RESPONSIVE LAYOUT VALUES
// ============================================
export interface ResponsiveLayout {
  // Touch targets (minimum 44px on mobile for accessibility)
  minTouchTarget: number;

  // Card widths
  cardWidth: number | string;
  cardMaxWidth: number;

  // Character card sizing (for selection screens)
  characterCardWidth: number;
  characterCardMinWidth: number;
  characterCardMaxWidth: number;

  // Form container sizing
  formContainerMaxWidth: number;

  // Modal sizing
  modalWidth: string;
  modalHeight: string;
  modalMaxWidth: number;
  modalBorderRadius: number;

  // Input sizing
  inputMinHeight: number;
  inputPadding: number;

  // Header sizing
  headerPadding: number;
  headerHeight: number;

  // Sidebar
  sidebarWidth: number;
  sidebarCollapsedWidth: number;

  // Grid columns
  gridColumns: number;
  gridGap: number;

  // Border radius scale
  borderRadiusSm: number;
  borderRadiusMd: number;
  borderRadiusLg: number;
}

export function calculateLayout(width: number): ResponsiveLayout {
  const isMobile = width < BREAKPOINTS.mobile;
  const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet;
  const isDesktop = width >= BREAKPOINTS.tablet && width < BREAKPOINTS.large;
  const isUltrawide = width >= BREAKPOINTS.large;

  return {
    // Touch targets - always at least 44px on touch devices
    minTouchTarget: isMobile ? 44 : 40,

    // Card widths - full width on mobile, fixed on larger screens
    cardWidth: isMobile ? '100%' : isTablet ? '48%' : isDesktop ? 200 : 280,
    cardMaxWidth: isMobile ? 9999 : isTablet ? 300 : isDesktop ? 220 : 350,

    // Character card sizing - for selection grids (2/3/4 columns)
    characterCardWidth: isMobile ? Math.floor((width - 48) / 2) : isTablet ? Math.floor((width - 64) / 3) : 180,
    characterCardMinWidth: isMobile ? 130 : isTablet ? 140 : 160,
    characterCardMaxWidth: isMobile ? 200 : isTablet ? 220 : 280,

    // Form container sizing - login/register cards
    formContainerMaxWidth: isMobile ? 9999 : isTablet ? 520 : isDesktop ? 600 : 720,

    // Modal sizing - full screen on mobile, larger max-width for ultrawide
    modalWidth: isMobile ? '100%' : isTablet ? '90%' : isDesktop ? '66.67%' : '50%',
    modalHeight: isMobile ? '100%' : isTablet ? '90%' : '80%',
    modalMaxWidth: isMobile ? 9999 : isTablet ? 600 : isDesktop ? 800 : 1200,
    modalBorderRadius: isMobile ? 0 : 20,

    // Input sizing
    inputMinHeight: isMobile ? 48 : 44,
    inputPadding: isMobile ? 14 : 12,

    // Header sizing
    headerPadding: isMobile ? 12 : isUltrawide ? 20 : 16,
    headerHeight: isMobile ? 56 : isUltrawide ? 72 : 64,

    // Sidebar - wider on ultrawide
    sidebarWidth: isMobile ? width : isUltrawide ? 280 : 224,
    sidebarCollapsedWidth: 56,

    // Grid - more columns on ultrawide
    gridColumns: isMobile ? 1 : isTablet ? 2 : isDesktop ? 3 : 4,
    gridGap: isMobile ? 12 : isUltrawide ? 20 : 16,

    // Border radius scale - smaller on mobile for tighter designs
    borderRadiusSm: isMobile ? 6 : 8,
    borderRadiusMd: isMobile ? 10 : 12,
    borderRadiusLg: isMobile ? 14 : 16,
  };
}

// ============================================
// ORIENTATION DETECTION
// ============================================
export type Orientation = 'portrait' | 'landscape';

export function getOrientation(width: number, height: number): Orientation {
  return width > height ? 'landscape' : 'portrait';
}

/**
 * Detect if device is in mobile landscape mode
 * This is when a phone is rotated landscape (height becomes short)
 * Key indicator: short height (< 500px) + landscape orientation
 * This hides header/tab bar to maximize screen space on phones
 */
export function isMobileLandscapeMode(width: number, height: number): boolean {
  const isLandscape = width > height;
  // Mobile landscape = short height (phone in landscape has ~400-500px height)
  // Don't check width - landscape phones can have 800-1000px width
  const shortHeight = height < 500;
  return isLandscape && shortHeight;
}

// ============================================
// RESPONSIVE HOOK
// ============================================
export interface ResponsiveValues {
  width: number;
  height: number;
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLandscape: boolean;
  isMobileLandscape: boolean;
  orientation: Orientation;
  fonts: ResponsiveFonts;
  spacing: ResponsiveSpacing;
  layout: ResponsiveLayout;
  borderRadius: ResponsiveBorderRadius;
  components: ComponentSizing;
  // Helper functions bound to current dimensions
  vw: (percent: number) => number;
  vh: (percent: number) => number;
  scalePx: (basePx: number, minPx?: number, maxPx?: number) => number;
  // Clamp helpers bound to current dimensions
  clampVw: (minPx: number, vwPercent: number, maxPx: number) => number;
  clampVh: (minPx: number, vhPercent: number, maxPx: number) => number;
  proportionalWidth: (percent: number, minPx?: number) => number;
}

export function useResponsive(): ResponsiveValues {
  const [dimensions, setDimensions] = useState({
    width: initialWidth,
    height: initialHeight,
  });

  useEffect(() => {
    const handleChange = ({ window }: { window: ScaledSize }) => {
      setDimensions({
        width: window.width,
        height: window.height,
      });
    };

    const subscription = Dimensions.addEventListener('change', handleChange);

    return () => {
      subscription?.remove();
    };
  }, []);

  return useMemo(() => {
    const { width, height } = dimensions;
    const deviceType = getDeviceType(width);
    const orientation = getOrientation(width, height);
    const isLandscape = orientation === 'landscape';
    const mobileLandscape = isMobileLandscapeMode(width, height);

    return {
      width,
      height,
      deviceType,
      isMobile: deviceType === 'narrow' || deviceType === 'mobile',
      isTablet: deviceType === 'tablet',
      isDesktop: deviceType === 'desktop' || deviceType === 'large',
      isLandscape,
      isMobileLandscape: mobileLandscape,
      orientation,
      fonts: calculateFonts(width),
      spacing: calculateSpacing(width),
      layout: calculateLayout(width),
      borderRadius: calculateBorderRadius(width),
      components: calculateComponentSizing(width, height),
      // Helper functions bound to current dimensions
      vw: (percent: number) => vw(percent, width),
      vh: (percent: number) => vh(percent, height),
      scalePx: (basePx: number, minPx?: number, maxPx?: number) =>
        scalePx(basePx, width, minPx, maxPx),
      // Clamp helpers bound to current dimensions
      clampVw: (minPx: number, vwPercent: number, maxPx: number) =>
        clampVw(minPx, vwPercent, maxPx, width),
      clampVh: (minPx: number, vhPercent: number, maxPx: number) =>
        clampVh(minPx, vhPercent, maxPx, height),
      proportionalWidth: (percent: number, minPx?: number) =>
        proportionalWidth(percent, width, minPx),
    };
  }, [dimensions]);
}

// ============================================
// VIEWPORT-RELATIVE SIZING HELPERS
// ============================================

/**
 * Convert percentage to viewport width pixels
 */
export function vw(percent: number, screenWidth: number): number {
  return Math.round((percent / 100) * screenWidth);
}

/**
 * Convert percentage to viewport height pixels
 */
export function vh(percent: number, screenHeight: number): number {
  return Math.round((percent / 100) * screenHeight);
}

/**
 * Scale a base pixel value proportionally to screen size
 * Uses 375px as the base width (iPhone SE reference)
 * @param basePx - Base pixel value at 375px width
 * @param screenWidth - Current screen width
 * @param minPx - Optional minimum value
 * @param maxPx - Optional maximum value
 */
export function scalePx(
  basePx: number,
  screenWidth: number,
  minPx?: number,
  maxPx?: number
): number {
  const BASE_WIDTH = 375;
  const scaled = Math.round(basePx * (screenWidth / BASE_WIDTH));
  if (minPx !== undefined && maxPx !== undefined) {
    return clamp(scaled, minPx, maxPx);
  }
  if (minPx !== undefined) return Math.max(scaled, minPx);
  if (maxPx !== undefined) return Math.min(scaled, maxPx);
  return scaled;
}

/**
 * Scale for both width and height (for square elements)
 * Uses the smaller scale factor to maintain proportions
 */
export function scalePxBoth(
  basePx: number,
  screenWidth: number,
  screenHeight: number,
  minPx?: number,
  maxPx?: number
): number {
  const BASE_WIDTH = 375;
  const BASE_HEIGHT = 667;
  const scaleW = screenWidth / BASE_WIDTH;
  const scaleH = screenHeight / BASE_HEIGHT;
  const scale = Math.min(scaleW, scaleH);
  const scaled = Math.round(basePx * scale);
  return clamp(scaled, minPx ?? Math.round(scaled * 0.5), maxPx ?? Math.round(scaled * 2));
}

// ============================================
// RESPONSIVE BORDER RADIUS
// ============================================
export interface ResponsiveBorderRadius {
  xs: number;   // 3px base (tiny elements)
  sm: number;   // 6px base (inputs, small buttons)
  md: number;   // 10px base (cards, modals)
  lg: number;   // 14px base (large cards)
  xl: number;   // 20px base (hero sections)
  xxl: number;  // 24px base (large rounded)
  full: number; // 9999 (circles)
}

export function calculateBorderRadius(width: number): ResponsiveBorderRadius {
  const scale = width < BREAKPOINTS.narrow ? 0.75
    : width < BREAKPOINTS.mobile ? 0.85
    : width < BREAKPOINTS.tablet ? 0.92
    : width < BREAKPOINTS.desktop ? 1.0
    : width < BREAKPOINTS.large ? 1.1
    : 1.45;

  return {
    xs: Math.round(3 * scale),
    sm: Math.round(6 * scale),
    md: Math.round(10 * scale),
    lg: Math.round(14 * scale),
    xl: Math.round(20 * scale),
    xxl: Math.round(24 * scale),
    full: 9999,
  };
}

// ============================================
// COMPONENT-SPECIFIC RESPONSIVE SIZING
// ============================================
export interface ComponentSizing {
  speechBubble: {
    // Proportional widths (percentage of screen)
    widthPercent: number;           // Base width as % of screen
    floatingWidthPercent: number;   // Floating bubble width
    stackedWidthPercent: number;    // Mobile stacked width
    // Min bounds
    minWidth: number;
    // Font sizes using clamp approach
    nameFontSize: number;
    textFontSize: number;
    // Spacing using clamp
    paddingHorizontal: number;
    paddingVertical: number;
    // Line height multiplier
    lineHeightMultiplier: number;
    // Legacy (kept for compatibility)
    maxWidth: number;
    padding: number;
    borderWidth: number;
    tailSize: number;
  };
  iconSizes: {
    xs: number;  // 14px base
    sm: number;  // 18px base
    md: number;  // 22px base
    lg: number;  // 30px base
    xl: number;  // 42px base
    xxl: number; // 64px base
  };
  buttonSizes: {
    sm: number;  // 32px base
    md: number;  // 40px base
    lg: number;  // 48px base
  };
  animationOffsets: {
    float: number;     // 8px base - Floating animation amplitude
    bounce: number;    // 16px base - Bounce animation amplitude
    slide: number;     // Slide-in distance (scaled to screen)
  };
}

export function calculateComponentSizing(
  width: number,
  height: number
): ComponentSizing {
  const scale = width < BREAKPOINTS.narrow ? 0.75
    : width < BREAKPOINTS.mobile ? 0.85
    : width < BREAKPOINTS.tablet ? 0.92
    : width < BREAKPOINTS.desktop ? 1.0
    : width < BREAKPOINTS.large ? 1.1
    : 1.45;

  // Determine proportional widths based on device size
  const isMobileSize = width < BREAKPOINTS.tablet;

  return {
    speechBubble: {
      // Proportional widths - scale with screen, no hard max
      widthPercent: isMobileSize ? 0.35 : 0.38,
      floatingWidthPercent: isMobileSize ? 0.30 : 0.35,
      stackedWidthPercent: 0.92, // Nearly full width for mobile stacked
      // Minimum width floor
      minWidth: clampVw(100, 25, 180, width),
      // Font sizes: clamp(min, preferred vw, max)
      nameFontSize: clampVw(11, 2.8, 22, width),
      textFontSize: clampVw(12, 3.2, 24, width),
      // Padding: clamp approach
      paddingHorizontal: clampVw(8, 2.5, 24, width),
      paddingVertical: clampVw(6, 2, 18, width),
      // Line height as multiplier
      lineHeightMultiplier: 1.45,
      // Legacy (kept for compatibility)
      maxWidth: Math.min(Math.round(380 * scale), width * 0.85),
      padding: Math.round(14 * scale),
      borderWidth: Math.round(2 * scale),
      tailSize: Math.round(10 * scale),
    },
    iconSizes: {
      xs: Math.round(14 * scale),
      sm: Math.round(18 * scale),
      md: Math.round(22 * scale),
      lg: Math.round(30 * scale),
      xl: Math.round(42 * scale),
      xxl: Math.round(64 * scale),
    },
    buttonSizes: {
      sm: Math.round(32 * scale),
      md: Math.round(40 * scale),
      lg: Math.round(48 * scale),
    },
    animationOffsets: {
      float: Math.round(8 * scale),
      bounce: Math.round(16 * scale),
      slide: Math.round(300 * (width / 375)),
    },
  };
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Returns different values based on device type
 */
export function responsive<T>(
  mobile: T,
  tablet: T,
  desktop: T,
  deviceType: DeviceType,
  ultrawide?: T
): T {
  switch (deviceType) {
    case 'narrow':
      return mobile;
    case 'mobile':
      return mobile;
    case 'tablet':
      return tablet;
    case 'desktop':
      return desktop;
    case 'large':
      return desktop;
    case 'ultrawide':
      return ultrawide ?? desktop;
    default:
      return desktop;
  }
}

/**
 * Calculate width as percentage of screen
 */
export function widthPercent(percent: number, screenWidth: number): number {
  return Math.round((percent / 100) * screenWidth);
}

/**
 * Calculate height as percentage of screen
 */
export function heightPercent(percent: number, screenHeight: number): number {
  return Math.round((percent / 100) * screenHeight);
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ============================================
// CLAMP-BASED RESPONSIVE SIZING (Hybrid Approach)
// ============================================

/**
 * Clamp a viewport-relative value between min and max pixel bounds
 * Mimics CSS clamp(min, preferred, max) behavior for React Native
 *
 * @param minPx - Minimum pixel value (floor)
 * @param vwPercent - Viewport width percentage for preferred value (e.g., 3 = 3vw)
 * @param maxPx - Maximum pixel value (ceiling)
 * @param screenWidth - Current screen width
 */
export function clampVw(
  minPx: number,
  vwPercent: number,
  maxPx: number,
  screenWidth: number
): number {
  const preferred = (vwPercent / 100) * screenWidth;
  return Math.round(clamp(preferred, minPx, maxPx));
}

/**
 * Clamp a viewport-relative value based on height
 *
 * @param minPx - Minimum pixel value
 * @param vhPercent - Viewport height percentage for preferred value
 * @param maxPx - Maximum pixel value
 * @param screenHeight - Current screen height
 */
export function clampVh(
  minPx: number,
  vhPercent: number,
  maxPx: number,
  screenHeight: number
): number {
  const preferred = (vhPercent / 100) * screenHeight;
  return Math.round(clamp(preferred, minPx, maxPx));
}

/**
 * Calculate proportional width as percentage of screen with optional min bound
 * NO hard-coded max cap - scales proportionally with screen
 *
 * @param percent - Percentage of screen width (e.g., 0.35 = 35%)
 * @param screenWidth - Current screen width
 * @param minPx - Optional minimum pixel value
 */
export function proportionalWidth(
  percent: number,
  screenWidth: number,
  minPx?: number
): number {
  let result = screenWidth * percent;
  if (minPx !== undefined) result = Math.max(result, minPx);
  return Math.round(result);
}

/**
 * Get responsive value based on width breakpoints
 */
export function getResponsiveValue<T>(
  width: number,
  values: { mobile?: T; tablet?: T; desktop?: T; default: T }
): T {
  if (width < BREAKPOINTS.mobile && values.mobile !== undefined) {
    return values.mobile;
  }
  if (width < BREAKPOINTS.tablet && values.tablet !== undefined) {
    return values.tablet;
  }
  if (values.desktop !== undefined) {
    return values.desktop;
  }
  return values.default;
}

// ============================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================
const Layout = {
  window: {
    width: initialWidth,
    height: initialHeight,
  },
  isSmallDevice: initialWidth < 375,
};

export default Layout;
